import type { Site } from '../core/modules/sites/Site';
import type { TabletopBlockRow } from '../core/types/TabletopBlockRow';
import type { WidgetProps } from '../core/types/WidgetProps';
import type { AiredaleFeedsGetter } from '../sharedModules/api/airedale/createAiredaleFeedsGetter';

import { buildTabletopBlocks, type BuildTabletopBlocksInput } from './buildTabletopBlocks';

export type PrepareTabletopWidgetPropsInput = BuildTabletopBlocksInput & {
  dealData: WidgetProps['dealData'];
  getModelOffersResponse: WidgetProps['getModelOffersResponse'];
  hawkJson: WidgetProps['hawkJson'];
};

type PrepareTabletopWidgetPropsResult<T extends PrepareTabletopWidgetPropsInput> = T & {
  tabletopBlocks: TabletopBlockRow[];
};

export const prepareTabletopWidgetProps = async <T extends PrepareTabletopWidgetPropsInput>(
  widgetProps: T,
  site: Site,
  getAiredaleFeeds: AiredaleFeedsGetter,
): Promise<PrepareTabletopWidgetPropsResult<T>> => {
  const { hawkJson } = widgetProps;
  const { buyingGuideBlocksData } = hawkJson;

  if (!buyingGuideBlocksData?.length) {
    return { ...widgetProps, tabletopBlocks: [] };
  }

  const reviewIds = buyingGuideBlocksData.flatMap((block) =>
    block.reviewId ? [block.reviewId] : [],
  );
  let buyingGuideBlocksWithReviewData = buyingGuideBlocksData;

  if (reviewIds.length > 0) {
    const airedaleData = await getAiredaleFeeds({
      site: site.id,
      articleId: reviewIds.join(','),
      fields: 'id,articleAuthor,articleAuthorUrl,canonical,createdDate',
      length: reviewIds.length,
    });

    buyingGuideBlocksWithReviewData = buyingGuideBlocksData.map((block) => {
      if (!block.reviewId) return block;

      const data = airedaleData.find((item) => item.id === block.reviewId);

      if (data) {
        return {
          ...block,
          authorName: data.articleauthor[0],
          authorUrl: data.articleauthorurl?.[0],
          reviewDate: data.createddate,
        };
      }
      return block;
    });
  }

  const propsWithReviewData: T = {
    ...widgetProps,
    hawkJson: {
      ...hawkJson,
      buyingGuideBlocksData: buyingGuideBlocksWithReviewData,
    },
  };

  const tabletopBlocks = await buildTabletopBlocks(
    propsWithReviewData,
    buyingGuideBlocksWithReviewData,
    propsWithReviewData.getModelOffersResponse,
  );
  const flat = tabletopBlocks.flatMap((r) => r.deals);
  const dealData: T['dealData'] = {
    ...propsWithReviewData.dealData,
    deals: flat,
    totalDeals: flat.length,
  };

  return {
    ...propsWithReviewData,
    tabletopBlocks,
    dealData,
  };
};
